import { ArrowRight, Play } from "lucide-react";
import { scrollToId, goToWaitlist } from "@/lib/site";
import { Reveal } from "./Reveal";

const STATS = [
  ["10M+", "PHOTOS PRESERVED"],
  ["250K+", "FAMILIES"],
  ["100+", "YEARS OF STORIES"],
];

const Backdrop = () => (
  <div className="relative h-[380px] w-full sm:h-[500px] lg:absolute lg:inset-y-0 lg:left-[24%] lg:right-0 lg:h-auto">
    <img
      src="/assets/memories_portrait.jpg"
      alt="A woman at dusk surrounded by a glowing ring of family photographs"
      data-testid="memories-image"
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover object-[68%_30%]"
    />
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#030507] via-[#030507]/10 to-transparent lg:bg-gradient-to-r lg:from-[#030507] lg:via-[#030507]/25" />
    <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#030507] to-transparent" />
    <div className="pointer-events-none absolute inset-x-0 bottom-0 hidden h-48 bg-gradient-to-t from-[#030507] to-transparent lg:block" />
  </div>
);

const Phone = () => (
  <div className="relative mx-auto mt-16 w-[190px] rotate-[7deg] sm:w-[230px] lg:absolute lg:bottom-[-20px] lg:right-[7%] lg:mt-0 lg:w-[250px] xl:right-[10%]">
    <img
      src="/assets/phone_app_v2_cut.webp"
      alt="The Chronoloop app showing a family timeline, family members and recent memories"
      data-testid="phone-image"
      loading="lazy"
      className="relative aspect-[470/1081] w-full [filter:drop-shadow(0_40px_50px_rgba(0,0,0,0.85))_drop-shadow(0_0_40px_rgba(231,201,157,0.18))]"
    />
  </div>
);

export const BuiltByMemories = () => (
  <section id="built-by-memories" data-testid="section-built-by-memories" className="relative overflow-hidden pb-24 lg:pb-0">
    <Backdrop />
    <div className="cl-container relative -mt-28 sm:-mt-36 lg:mt-0 lg:flex lg:min-h-[880px] lg:items-center lg:py-32">
      <div className="max-w-xl">
        <Reveal>
          <p className="cl-kicker">A FAMILY ARCHIVE</p>
          <h2 data-testid="built-heading" className="cl-heading mt-5 text-[2.3rem] leading-[1.1] sm:text-5xl lg:text-[3.5rem]">
            <span className="block text-white">Built by memories.</span>
            <span className="block text-white">Powered by AI.</span>
            <span className="block text-[#79B9FF]">Made for generations.</span>
          </h2>
        </Reveal>
        <Reveal delay={120}>
          <p className="mt-7 max-w-md text-[15px] leading-relaxed text-[#B7C3D1] sm:text-base">
            From everyday moments to life's biggest milestones, Chronoloop helps you rediscover what matters — and keeps
            it alive for the people who come next.
          </p>
        </Reveal>
        <Reveal delay={220} className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-5">
          <a href="#launching-soon" onClick={goToWaitlist} data-testid="built-start-journey-button" className="btn-gold">
            Start Your Journey <ArrowRight size={16} strokeWidth={1.4} />
          </a>
          <a
            href="#how-it-works"
            onClick={(e) => {
              e.preventDefault();
              scrollToId("how-it-works");
            }}
            data-testid="see-how-it-works-button"
            className="btn-link"
          >
            See How It Works <Play size={12} strokeWidth={1.5} />
          </a>
        </Reveal>
        <Reveal delay={320} className="mt-14 grid max-w-lg grid-cols-3 border-t border-[#1C2A3A] pt-8">
          {STATS.map(([num, label], i) => (
            <div key={label} data-testid={`stat-${i}`} className={`pr-2 ${i ? "border-l border-[#1C2A3A] pl-4 sm:pl-7" : ""}`}>
              <p className="font-display cl-gold-text text-3xl font-light sm:text-[2.6rem]">{num}</p>
              <p className="mt-2 text-[9.5px] font-medium leading-snug tracking-[0.2em] text-[#9AAABD] sm:text-[10.5px]">{label}</p>
            </div>
          ))}
        </Reveal>
      </div>
      <Phone />
    </div>
  </section>
);
